import { Component, computed, effect, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { switchMap } from 'rxjs';
import { PageShellComponent } from '@shared/components/page-shell/page-shell.component';
import { ChartCardComponent } from '@shared/components/chart-card/chart-card.component';
import { RegbrFilterListComponent } from './components/regbr-filter-list.component';
import { RegbrTabsComponent } from './components/regbr-tabs.component';
import { RegbrService, optionValues } from './regbr.service';
import type { RegbrChartOption, RegbrChartParams, RegbrGroupBy } from './regbr.models';

@Component({
  selector: 'app-regbr-page',
  standalone: true,
  imports: [
    TranslocoPipe,
    PageShellComponent,
    ChartCardComponent,
    RegbrFilterListComponent,
    RegbrTabsComponent,
  ],
  templateUrl: './regbr.page.html',
})
export class RegbrPage {
  private readonly service = inject(RegbrService);
  private readonly transloco = inject(TranslocoService);
  private readonly lang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });
  private readonly filters = toSignal(
    toObservable(this.lang).pipe(switchMap(() => this.service.getFilters())),
  );
  private readonly initialized = signal(false);

  protected readonly breadcrumb = [{ labelKey: 'nav.items.regbr' }];
  protected readonly groupBy = signal<RegbrGroupBy>('agencia_reguladora');
  protected readonly agencias = signal<string[]>([]);
  protected readonly tiposLei = signal<string[]>([]);
  protected readonly setores = signal<string[]>([]);
  protected readonly situacoes = signal<string[]>([]);
  protected readonly chartOptions = signal<RegbrChartOption>(null);
  protected readonly loadingChart = signal(false);

  protected readonly groupOptions: { value: RegbrGroupBy; labelKey: string }[] = [
    {
      value: 'agencia_reguladora',
      labelKey: 'pages.regbr.filters.groupByAgency',
    },
    {
      value: 'tipo_lei',
      labelKey: 'pages.regbr.filters.groupByActType',
    },
    {
      value: 'setor',
      labelKey: 'pages.regbr.filters.groupBySector',
    },
  ];

  protected readonly activeLawOptions = computed(() => {
    const filters = this.filters();
    if (!filters) return [];
    return this.groupBy() === 'agencia_reguladora'
      ? filters.leisAgenciaReguladora
      : filters.leis;
  });

  protected readonly params = computed<RegbrChartParams>(() => ({
    agrupador: this.groupBy(),
    agencias: this.agencias(),
    tiposLei: this.tiposLei(),
    setores: this.setores(),
    situacoes: this.situacoes(),
  }));

  protected readonly canLoadChart = computed(() => {
    const params = this.params();
    if (!this.filters()) return false;
    if (params.tiposLei.length === 0 || params.situacoes.length === 0) return false;
    return params.agrupador === 'agencia_reguladora'
      ? params.agencias.length > 0
      : params.setores.length > 0;
  });

  constructor() {
    effect(() => {
      const filters = this.filters();
      if (!filters || this.initialized()) return;

      this.initialized.set(true);
      this.tiposLei.set(optionValues(filters.leisAgenciaReguladora));
      this.agencias.set(optionValues(filters.agencias));
      this.setores.set(optionValues(filters.setores));
      this.situacoes.set(optionValues(filters.situacoes));
    });

    effect((onCleanup) => {
      const params = this.params();
      if (!this.canLoadChart()) return;

      this.loadingChart.set(true);
      this.chartOptions.set(null);

      const sub = this.service.getChart(params).subscribe({
        next: (chart) => {
          this.chartOptions.set(chart);
          this.loadingChart.set(false);
        },
        error: () => {
          this.chartOptions.set(null);
          this.loadingChart.set(false);
        },
      });

      onCleanup(() => sub.unsubscribe());
    });
  }

  protected setGroupBy(value: RegbrGroupBy): void {
    if (this.groupBy() === value) return;
    this.groupBy.set(value);

    const filters = this.filters();
    if (!filters) return;

    this.tiposLei.set(
      value === 'agencia_reguladora'
        ? optionValues(filters.leisAgenciaReguladora)
        : optionValues(filters.leis),
    );
  }

  protected setAgencias(values: string[]): void {
    this.agencias.set(values);
  }

  protected setTiposLei(values: string[]): void {
    this.tiposLei.set(values);
  }

  protected setSetores(values: string[]): void {
    this.setores.set(values);
  }

  protected setSituacoes(values: string[]): void {
    this.situacoes.set(values);
  }

  protected resetFilters(): void {
    const filters = this.filters();
    if (!filters) return;

    this.tiposLei.set(optionValues(this.activeLawOptions()));
    this.agencias.set(optionValues(filters.agencias));
    this.setores.set(optionValues(filters.setores));
    this.situacoes.set(optionValues(filters.situacoes));
  }

  protected get agencyOptions() {
    return this.filters()?.agencias ?? [];
  }

  protected get sectorOptions() {
    return this.filters()?.setores ?? [];
  }

  protected get situationOptions() {
    return this.filters()?.situacoes ?? [];
  }
}
